import { addDays, daysBetween, instantToLocalDate, instantToLocalTime, localToInstant, weekdayOf } from './time.ts';
import type { LocalDate } from './time.ts';
import type { AvailabilityBlock } from './types.ts';
import type { BlockInput, TemplateBlock } from './validation.ts';

/**
 * Calendar shortcuts. All copies preserve WALL-CLOCK times, so copying a
 * 6 AM–2 PM block across a DST change still lands at 6 AM–2 PM.
 */

type BlockLike = Pick<AvailabilityBlock, 'start' | 'end' | 'status' | 'vehicleId' | 'destinations' | 'notes'>;

/** Move a block by whole local days, keeping its wall-clock start/end. */
export function shiftBlockByDays(b: BlockLike, days: number): BlockInput {
  const sDate = instantToLocalDate(b.start);
  const eDate = instantToLocalDate(b.end);
  return {
    start: localToInstant(addDays(sDate, days), instantToLocalTime(b.start)),
    end: localToInstant(addDays(eDate, days), instantToLocalTime(b.end)),
    status: b.status,
    vehicleId: b.vehicleId,
    destinations: b.destinations.map((d) => ({ ...d })),
    notes: b.notes ?? null,
  };
}

/** Blocks that START on the given local date. */
export function blocksStartingOn<T extends BlockLike>(blocks: T[], date: LocalDate): T[] {
  return blocks.filter((b) => instantToLocalDate(b.start) === date);
}

/** Copy one day's blocks to another day ("copy yesterday" = copyDay(yesterday, today)). */
export function copyDay(blocks: BlockLike[], fromDate: LocalDate, toDate: LocalDate): BlockInput[] {
  const delta = daysBetween(fromDate, toDate);
  return blocksStartingOn(blocks, fromDate).map((b) => shiftBlockByDays(b, delta));
}

/** Copy every block starting in the week beginning fromMonday to the week beginning toMonday. */
export function copyWeek(blocks: BlockLike[], fromMonday: LocalDate, toMonday: LocalDate): BlockInput[] {
  const delta = daysBetween(fromMonday, toMonday);
  const out: BlockInput[] = [];
  for (let i = 0; i < 7; i++) {
    for (const b of blocksStartingOn(blocks, addDays(fromMonday, i))) out.push(shiftBlockByDays(b, delta));
  }
  return out;
}

/** Apply one block to several target dates (e.g. Mon, Wed, Fri of a week). */
export function applyToDates(b: BlockLike, targetDates: LocalDate[]): BlockInput[] {
  const from = instantToLocalDate(b.start);
  return targetDates.filter((d) => d !== from).map((d) => shiftBlockByDays(b, daysBetween(from, d)));
}

/** Duplicate a block onto the next free slot of the same day: directly after it. */
export function duplicateAfter(b: BlockLike): BlockInput {
  const len = b.end - b.start;
  return { start: b.end, end: b.end + len, status: b.status, vehicleId: b.vehicleId, destinations: b.destinations.map((d) => ({ ...d })), notes: b.notes ?? null };
}

/** Turn blocks into a reusable template relative to an anchor date (Monday for week templates). */
export function blocksToTemplate(blocks: BlockLike[], anchor: LocalDate): TemplateBlock[] {
  return blocks
    .map((b) => {
      const sDate = instantToLocalDate(b.start);
      const eDate = instantToLocalDate(b.end);
      const dayOffset = daysBetween(anchor, sDate);
      const endDayOffset = daysBetween(sDate, eDate);
      return {
        dayOffset,
        localStart: instantToLocalTime(b.start),
        localEnd: instantToLocalTime(b.end),
        endDayOffset: (endDayOffset >= 1 ? 1 : 0) as 0 | 1,
        status: b.status,
        vehicleId: b.vehicleId,
        destinations: b.destinations.map((d) => ({ ...d })),
        notes: b.notes ?? null,
      };
    })
    .filter((t) => t.dayOffset >= 0 && t.dayOffset <= 6);
}

/** Materialize a template at an anchor date. */
export function templateToBlocks(template: TemplateBlock[], anchor: LocalDate): BlockInput[] {
  return template.map((t) => {
    const date = addDays(anchor, t.dayOffset);
    return {
      start: localToInstant(date, t.localStart),
      end: localToInstant(addDays(date, t.endDayOffset), t.localEnd),
      status: t.status,
      vehicleId: t.vehicleId,
      destinations: t.destinations.map((d) => ({ ...d })),
      notes: t.notes ?? null,
    };
  });
}

/** Dates in a week matching ISO weekdays (helper for "apply to weekdays"). */
export function datesForWeekdays(monday: LocalDate, weekdays: number[]): LocalDate[] {
  const set = new Set(weekdays);
  const out: LocalDate[] = [];
  for (let i = 0; i < 7; i++) {
    const d = addDays(monday, i);
    if (set.has(weekdayOf(d))) out.push(d);
  }
  return out;
}

/** True when two half-open intervals overlap. */
export function overlaps(a: { start: number; end: number }, b: { start: number; end: number }): boolean {
  return a.start < b.end && b.start < a.end;
}
